'use client';
import React from 'react';
import { Tabs } from '@/components/home/Tabs';
import headerTabs from '@/components/home/headerTabs';
import { TabWithSideImageContent } from '@/components/home/TabWithSideImageContent';
import { AgenticControlIcon, UnifiedIcon, ZeroTrustIcon } from './icons';
import Button from '../common/Button';
import Chevron from '../../../public/assets/icons/chevron-right.svg';
import useHeroSlider, {
  HeroBackgrounds,
  HeroSliderControls,
} from './HeroBackgroundSlider';
import { heroBackgrounds } from './heroBackgrounds';

const HeroItem = ({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) => {
  return (
    <div className="flex items-center gap-3.5">
      <HeroIcon>{icon}</HeroIcon>
      <span
        className="bg-clip-text text-lg text-transparent lg:text-xl"
        style={{
          backgroundImage: 'linear-gradient(90deg, #B8E8FA 0%, #7D94FC 100%)',
        }}
      >
        {label}
      </span>
    </div>
  );
};

const HeroIcon = ({ children }: { children?: React.ReactNode }) => {
  return (
    <div className="relative flex shrink-0 items-center justify-center [&>svg]:size-[70px]">
      {children}
    </div>
  );
};

const HeroIconDivider = () => {
  return (
    <svg
      width="2"
      height="88"
      viewBox="0 0 2 88"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="hidden md:block"
    >
      <path
        opacity="0.4"
        d="M0.864258 0L0.864248 87.0156"
        stroke="url(#paint0_linear_8984_1792)"
        strokeWidth="1.72879"
      />
      <defs>
        <linearGradient
          id="paint0_linear_8984_1792"
          x1="0.864258"
          y1="-3.42196"
          x2="-0.0406712"
          y2="66.062"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#1A4AC7" stopOpacity="0.4" />
          <stop offset="0.451951" stopColor="#B8E8FA" />
          <stop offset="1" stopColor="#1A4AC7" stopOpacity="0.4" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const staticHeroItems = [
  {
    icon: <AgenticControlIcon />,
    label: 'Agentic Control',
  },
  { icon: <UnifiedIcon />, label: 'Unified' },
  { icon: <ZeroTrustIcon />, label: 'Zero Trust' },
];

const StaticHero = () => {
  const { active, goTo, next, prev, setPaused } = useHeroSlider(
    heroBackgrounds.length
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      next();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prev();
    }
  };

  return (
    <section
      className="w-full"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={onKeyDown}
    >
      <div className="relative overflow-hidden bg-[#02021a]">
        <div className="-z-10">
          <HeroBackgrounds active={active} />
        </div>
        {/* 642px is where the artwork stops in Figma: its bottom edge lands
            level with the foot of the tab strip on the card below, which sinks
            100px into it. */}
        <div className="relative flex min-h-[40.125rem] flex-col px-4 pt-40 pb-40 text-white min-[1180px]:pb-0 sm:px-10">
          <div>
            <h1 className="text-h4 sm:text-h3 mb-5 lg:text-[72px]">
              Control What’s Next
            </h1>
            <p className="lg:text-h6 mt-1 max-w-xl text-[24px] font-[300] sm:text-xl">
              Unified Zero Trust Control Plane
            </p>
          </div>
          {/* The top margin is what places the row inside the hero: Figma puts
              the controls 53px clear of the card that overlaps the hero, and
              with a 700px hero and a 157px overlap that lands the row here. */}
          <div className="mt-14 flex flex-col flex-wrap gap-8 py-3 md:mt-35 md:flex-row md:items-center">
            {staticHeroItems.map((item, index) => (
              <React.Fragment key={item.label}>
                <HeroItem icon={item?.icon} label={item?.label} />
                {index < staticHeroItems.length - 1 && <HeroIconDivider />}
              </React.Fragment>
            ))}
            {/* Per the Figma hero the controls share the icon row and sit in
                from the edge — the design keeps their right inset at ~1.8x the
                copy's left gutter, which is 72px against our 40px one. On
                mobile they sit under the stacked icons. */}
            <HeroSliderControls
              active={active}
              goTo={goTo}
              next={next}
              prev={prev}
              className="self-end md:mr-8 md:ml-auto md:self-auto"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default function HomeHeader() {
  return (
    <>
      <StaticHero />
      {/* Figma sinks the card exactly one tab strip into the hero, so the
          artwork ends on the line under the tabs. The bottom margin is only
          for the stacked layout; wider, the page column's own padding is
          already the gap the design asks for. */}
      <section className="-mt-25 mb-10 w-full px-4 sm:px-10 md:mb-0 lg:h-125">
        <div className="h-full overflow-hidden shadow lg:flex">
          <div
            className="flex w-full shrink-0 flex-col justify-center gap-6 border-t border-l border-gray-300/15 px-10.5 py-12 text-white lg:w-80"
            style={{
              background:
                'linear-gradient(180deg,  rgba(10, 10, 10, 100) 0%, rgba(67, 67, 67, 100) 40%, rgba(46, 46, 46, 100) 100%)',
              backdropFilter: 'blur(64px)',
              WebkitBackdropFilter: 'blur(64px)',
            }}
          >
            <h2 className="text-h5">
              Get Built-in Security Control from Identity to Access
            </h2>
            <p className="text-[14px] font-light text-[#FFFFFF99]">
              Want to see whether your business qualifies for a Primary trial?
              Tell us what you are running today and a member of our team will
              take it from there.
            </p>

            {/* Gradient kept inline: its geometry is tuned to this wide button
                and differs from the shared `radial` Button variant. */}
            <Button
              href="/contact"
              className="w-full justify-center text-base font-medium"
              style={{
                background:
                  'radial-gradient(135.44% 146.88% at 52.5% 0%, #89D9F7 0%, #3576CD 39.4%, #4D4DBF 61.91%, #0000A3 100%)',
                boxShadow: [
                  'inset 0 0 0 1px #89D9F729',
                  'inset 0 1px 0 0 #FFFFFF33',
                  '0 1px 2px 0 #14151A0D',
                  '0 4px 14px -4px #0000A340',
                ].join(', '),
              }}
            >
              Contact Us
              <span className="pl-2">
                <Chevron />
              </span>
            </Button>
          </div>
          <Tabs
            dataset={headerTabs}
            type="card"
            render={(tabData) => (
              <TabWithSideImageContent
                heading={tabData.heading}
                description={tabData?.description}
                bullets={tabData.bullets}
                Diagram={tabData.Diagram}
                key={tabData.heading}
                bgDecoration={tabData.bgDecoration}
              />
            )}
          />
        </div>
      </section>
    </>
  );
}
